import Phaser from 'phaser';
import { SCENE_KEYS } from '@/config/constants';
import { getAllBubbleTypes, getBubbleConfig, getAllBubbleSounds } from '@/types/BubbleTypes';
import { getAllPowerupAssets } from '@/types/PowerupTypes';
import { getAllObstacleAssets } from '@/types/ObstacleTypes';
import { AnimationManager, FRAME_KEYS } from '@/utils/AnimationManager';
import { AudioManager } from '@/utils/AudioManager';
import { bakeAllBubbleEdges } from '@/utils/BubbleEdgeFalloff';

/**
 * Boot Scene - Asset Loading
 * Loads all game assets before starting the game
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_KEYS.BOOT });
  }

  preload(): void {
    // Show loading progress
    this.createLoadingScreen();

    // Track asset failures so we never silently boot with critical assets
    // missing in a 12h exhibition kiosk run. Anything matching the optional
    // allowlist (ambient life, parallax, decorative variants) is allowed to
    // fail because the consumers already no-op on missing textures.
    const OPTIONAL_PATTERNS = [
      /\/ambient\//,
      /\/parallax\//,
      /\/poseidon\/(victory|angry|idle_alt|flinch|victory_clip)\//,
      /\/sky\/storm_sky_layer_/,
      /\/decorative\//,
    ];
    // Per-asset retry. A flaky connection drops the occasional request; without
    // a retry that asset is simply gone for the whole session (the boss frames
    // were the usual casualty, which soft-locked the Hydra fight). Re-queue any
    // failed REQUIRED asset a couple of times before giving up. Optional assets
    // are allowed to fail silently and are never retried, saving bandwidth on a
    // poor link. Re-adding mid-load is supported by Phaser: 'complete' does not
    // fire until the requeued file resolves, so the boot waits for the retries.
    const MAX_RETRY = 2;
    const retryCounts: Record<string, number> = {};
    const requeue = (file: any): void => {
      const key: string = file?.key;
      const url: string = file?.url || file?.src;
      if (!key || !url) return;
      try {
        switch (file.type) {
          case 'audio': this.load.audio(key, url); break;
          case 'video': this.load.video(key, url); break;
          case 'font': this.load.font(key, url); break;
          case 'svg': this.load.svg(key, url, file.config); break;
          case 'image':
          default: this.load.image(key, url); break;
        }
      } catch (e) {
        console.warn(`⚠️ Re-queue failed for '${key}':`, e);
      }
    };

    let failedRequired = 0;
    this.load.on('loaderror', (file: any) => {
      const path: string = file?.src || file?.url || '';
      if (OPTIONAL_PATTERNS.some((re) => re.test(path))) {
        console.warn(`⚠️ Optional asset missing: ${path}`);
        return;
      }
      const key: string = file?.key || path;
      const n = (retryCounts[key] || 0) + 1;
      if (n <= MAX_RETRY) {
        retryCounts[key] = n;
        console.warn(`🔁 Retry ${n}/${MAX_RETRY} for required asset '${key}' (${path})`);
        requeue(file);
        return;
      }
      failedRequired++;
      console.error(`❌ Asset load failed after ${MAX_RETRY} retries (${failedRequired}): ${path}`);
    });

    // Loader-stall watchdog. The kiosk must never sit forever on the loading
    // screen, but the previous fixed 60s wall-clock reset punished slow (but
    // healthy) connections: it dropped whatever was still queued, namely the
    // late-queued boss art, and booted broken. Watch FORWARD PROGRESS instead.
    // A load that keeps advancing is allowed to finish (up to a hard ceiling),
    // and only a genuinely stuck loader (no file resolved for a sustained
    // window) is force-continued. Uses window timers because the scene time
    // system is not active until create().
    let bootCompleted = false;
    let lastProgressAt = Date.now();
    const bootStartedAt = lastProgressAt;
    const NO_PROGRESS_MS = 25_000;   // no file resolved for this long = stuck
    const ABSOLUTE_MAX_MS = 180_000; // hard ceiling so the kiosk never hangs
    this.load.on('progress', () => { lastProgressAt = Date.now(); });
    const stallCheck = window.setInterval(() => {
      if (bootCompleted) { window.clearInterval(stallCheck); return; }
      const now = Date.now();
      const stalledFor = now - lastProgressAt;
      const totalFor = now - bootStartedAt;
      if (stalledFor >= NO_PROGRESS_MS || totalFor >= ABSOLUTE_MAX_MS) {
        console.error(`⏱ Asset loader force-continuing (no progress ${Math.round(stalledFor / 1000)}s, total ${Math.round(totalFor / 1000)}s)`);
        window.clearInterval(stallCheck);
        // reset() drops queued files which makes the loader emit 'complete'.
        try { this.load.reset(); } catch {}
      }
    }, 2000);
    this.load.once('complete', () => {
      bootCompleted = true;
      window.clearInterval(stallCheck);
      if (failedRequired > 0) {
        console.warn(`⚠️ Booted with ${failedRequired} failed required assets. Game may render missing textures as blank.`);
      }
    });

    console.log('📦 Loading Poseidon idle animation frames...');

    // Load IDLE animation frames (60 individual frames at 600×760)
    // Place your files as: idle_1.png, idle_2.png, ..., idle_60.png
    // in the directory: public/assets/sprites/poseidon/idle/
    for (let i = 1; i <= 60; i++) {
      this.load.image(
        `${FRAME_KEYS.PLAYER.IDLE}_${i}`,
        `assets/sprites/poseidon/idle/idle_${i}.png`
      );
    }

    // Load WALK animation frames (7 frames)
    // Place walk frames as walk_1.png, walk_2.png, etc. in public/assets/sprites/poseidon/walk/
    console.log('📦 Loading Poseidon walk animation frames...');
    for (let i = 1; i <= 7; i++) {
      this.load.image(`${FRAME_KEYS.PLAYER.WALK}_${i}`, `assets/sprites/poseidon/walk/walk_${i}.png`);
    }

    // Load SHOOT animation frames (6 frames)
    // Place shoot frames as shoot_1.png, shoot_2.png, etc. in public/assets/sprites/poseidon/shoot/
    console.log('📦 Loading Poseidon shoot animation frames...');
    for (let i = 1; i <= 6; i++) {
      this.load.image(`${FRAME_KEYS.PLAYER.SHOOT}_${i}`, `assets/sprites/poseidon/shoot/shoot_${i}.png`);
    }

    // POLISH PASS: Reactive poses — flinch, victory, angry, idle variants
    console.log('📦 Loading Poseidon reactive poses...');
    for (let i = 1; i <= 2; i++) {
      this.load.image(`poseidon_flinch_${i}`, `assets/sprites/poseidon/flinch/flinch_${i}.png`);
      this.load.image(`poseidon_victory_${i}`, `assets/sprites/poseidon/victory/victory_${i}.png`);
      this.load.image(`poseidon_angry_${i}`, `assets/sprites/poseidon/angry/angry_${i}.png`);
      this.load.image(`poseidon_idle_alt_${i}`, `assets/sprites/poseidon/idle_alt/idle_alt_${i}.png`);
    }

    // Boss-victory hero clip — Poseidon raises the V sign (30 transparent
    // frames extracted from a generated clip). Plays once on the boss
    // victory screen; see AnimationManager.PLAYER.VICTORY_CLIP.
    console.log('📦 Loading Poseidon victory clip frames...');
    for (let i = 1; i <= 30; i++) {
      this.load.image(`poseidon_victory_clip_${i}`, `assets/sprites/poseidon/victory_clip/victory_clip_${i}.png`);
    }

    // World 3 storm-sky overlay — 3 dark cloud layers that fade as bubbles pop.
    // Generated by scripts/generate-storm-sky-clouds.py (Gemini + Fal PixelCut).
    console.log('📦 Loading World 3 storm sky cloud layers...');
    for (let i = 1; i <= 3; i++) {
      this.load.image(`storm_sky_layer_${i}`, `assets/sprites/sky/storm_sky_layer_${i}.png`);
    }

    // Gold powerup ball + 6 monochrome embossed icon overlays.
    // The gold ball is a single shared sprite reused for every powerup type;
    // the icon overlay tells the player WHICH powerup is in the bubble.
    // Generated by scripts/generate-powerup-balls.py (Gemini + Fal PixelCut).
    console.log('📦 Loading gold powerup ball + embossed icons...');
    this.load.image('gold_powerup_ball', 'assets/ui/powerups/gold_powerup_ball.png');
    const embossIcons = ['extralife', 'timefreeze', 'oceanshield', 'ecoblast', 'rapidfire', 'score2x'];
    for (const key of embossIcons) {
      this.load.image(`gold_emboss_${key}`, `assets/ui/powerups/gold_emboss_${key}.png`);
    }

    // Load background videos
    console.log('📦 Loading background videos...');
    this.load.video('bg_video_world1_video', 'game-content/world1 video.mp4');
    this.load.video('bg_video_world3_video', 'assets/backgrounds/videos/ship-stage_prob4.mp4');
    // World 2 — Aegean Shores (stages 3 + 4)
    this.load.video('bg_video_world2_video', 'assets/backgrounds/videos/world2.mp4');
    // Boss arena — Pollution Hydra's lair (key must match BackgroundManager's
    // `bg_video_${path.replace(/[^a-zA-Z0-9]/g, '_')}` scheme)
    this.load.video('bg_video_hydra_stage_mp4', 'assets/backgrounds/videos/hydra-stage.mp4');

    // Load custom fonts
    console.log('📦 Loading custom fonts...');
    this.load.font('CF Asty Std Book', 'assets/fonts/cf-asty/CFAstyStd-Book.ttf');
    this.load.font('CF Asty Std', 'assets/fonts/cf-asty/CFAstyStd-Bold.ttf');
    this.load.font('CF Asty Std Black', 'assets/fonts/cf-asty/CFAstyStd-Black.ttf');
    this.load.font('Cormorant Garamond', 'assets/fonts/CormorantGaramond-Regular.ttf');
    // Skranji — chunky display face for win/loss titles ("GAME OVER",
    // "STAGE 1 CLEARED", "FINAL SCORE", "TOTAL SCORE")
    this.load.font('Skranji', 'assets/fonts/skranji/Skranji-Regular.ttf');
    this.load.font('Skranji Bold', 'assets/fonts/skranji/Skranji-Bold.ttf');

    console.log('📦 Loading UI elements...');

    // HUD — kept as fallback for v2 life gem textures
    this.load.image('heart_full_v2', 'assets/ui/hud/heart_full_v2.png');
    this.load.image('heart_empty_v2', 'assets/ui/hud/heart_empty_v2.png');

    // Leaderboard & Name Entry (no v2 replacements yet)
    this.load.image('leaderboard_frame', 'assets/ui/leaderboard/leaderboard_frame.png');
    this.load.image('medal_gold', 'assets/ui/leaderboard/medal_gold.png');
    this.load.image('medal_silver', 'assets/ui/leaderboard/medal_silver.png');
    this.load.image('medal_bronze', 'assets/ui/leaderboard/medal_bronze.png');
    this.load.image('name_input_frame', 'assets/ui/leaderboard/name_input_frame.png');
    this.load.image('key_tile', 'assets/ui/leaderboard/key_tile.png');

    // ─── OCEANIC OVERWATCH v2 UI KIT ─────────────────────────────────
    // Oceanic Hearthstone overhaul. See docs/UI_OVERHAUL_MASTERPLAN.md
    console.log('📦 Loading Triple-A v2 UI kit...');

    // Buttons (4 hero variants × 3 states = 12)
    for (const variant of ['hero_gold', 'hero_danger', 'standard', 'compact']) {
      for (const state of ['normal', 'hover', 'pressed']) {
        const k = `btn_${variant}_${state}`;
        this.load.image(k, `assets/ui/v2/buttons/${k}.png`);
      }
    }
    this.load.image('btn_icon_normal', 'assets/ui/v2/buttons/btn_icon_normal.png');
    this.load.image('btn_icon_hover', 'assets/ui/v2/buttons/btn_icon_hover.png');
    this.load.image('btn_icon_pressed', 'assets/ui/v2/buttons/btn_icon_pressed.png');

    // Frames (only the two actually rendered in scenes)
    this.load.image('frame_scroll', 'assets/ui/v2/frames/frame_scroll.png');
    this.load.image('frame_modal_info', 'assets/ui/v2/frames/frame_modal_info.png');

    // HUD atomics (life gems — score/timer/combo/etc. are code-drawn)
    this.load.image('hud_life_gem_full', 'assets/ui/v2/hud/hud_life_gem_full.png');
    this.load.image('hud_life_gem_empty', 'assets/ui/v2/hud/hud_life_gem_empty.png');

    // Victory star
    this.load.image('star_v2_earned', 'assets/ui/v2/icons/star_earned.png');

    // Boss v2 (frame + fill — name banner retired, phase marker is code-drawn)
    this.load.image('boss_v2_hp_frame', 'assets/ui/v2/boss/boss_hp_frame.png');
    this.load.image('boss_v2_hp_fill', 'assets/ui/v2/boss/boss_hp_fill.png');

    // Title logo — crystal hero lettering
    this.load.image('title_logo_v4', 'assets/ui/v2/title/title_logo_v4.png');

    // HELMEPA partner logo (PNG version is the new menu hero)
    this.load.image('helmepa_logo', 'assets/ui/menu/helmepa_logo.png');

    // Menu hero assets — Poseidon character + chunky cyan/yellow buttons
    this.load.image('menu_poseidon_character', 'assets/ui/menu/poseidon_character.png');
    this.load.image('menu_btn_rectangular', 'assets/ui/menu/btn_rectangular.png');
    this.load.image('menu_btn_pill', 'assets/ui/menu/btn_pill.png');
    this.load.image('menu_btn_yellow_bubble', 'assets/ui/menu/btn_yellow_bubble.png');
    // Canonical CTA button art (vector). Rasterized at 2x native for crispness.
    // big-button -> large size, button -> regular sizes. (see src/ui/PillButton.ts)
    this.load.svg('btn_svg_large', 'assets/ui/big-button.svg', { width: 792, height: 318 });
    this.load.svg('btn_svg_regular', 'assets/ui/button.svg', { width: 898, height: 242 });

    // Premium v2 Sound Effects — only the ones actually triggered in scene code
    console.log('🔊 Loading premium v2 SFX...');
    const v2Sfx = [
      'sfx_btn_hover_wood', 'sfx_btn_click_heavy',
      'sfx_number_tick_small', 'sfx_number_thunk_big',
      'sfx_star_chime_1', 'sfx_star_chime_2', 'sfx_star_chime_3',
      'sfx_curtain_heavy_down', 'sfx_curtain_heavy_up',
      'sfx_page_turn',
      'sfx_thunder_crack', 'sfx_boss_phase_shatter',
      'sfx_fanfare_common', 'sfx_fanfare_rare',
      'sfx_fanfare_epic', 'sfx_fanfare_legendary',
      'sfx_heart_shatter',
    ];
    for (const k of v2Sfx) this.load.audio(k, `assets/audio/sfx/v2/${k}.mp3`);

    // ─── AAA UI Sound Effects ────────────────────────────────
    console.log('📦 Loading UI sound effects...');
    this.load.audio('sfx_btn_click', 'assets/audio/sfx/overwatch/sfx_btn_click.mp3');
    this.load.audio('sfx_btn_hover', 'assets/audio/sfx/overwatch/sfx_btn_hover.mp3');
    this.load.audio('sfx_modal_open', 'assets/audio/sfx/overwatch/sfx_modal_open.mp3');
    this.load.audio('sfx_modal_close', 'assets/audio/sfx/overwatch/sfx_modal_close.mp3');
    this.load.audio('sfx_score_tick', 'assets/audio/sfx/overwatch/sfx_score_tick.mp3');
    this.load.audio('sfx_stage_clear', 'assets/audio/sfx/overwatch/sfx_stage_clear.mp3');
    this.load.audio('sfx_game_over', 'assets/audio/sfx/overwatch/sfx_game_over.mp3');
    this.load.audio('sfx_star_earn', 'assets/audio/sfx/overwatch/sfx_star_earn.mp3');
    this.load.audio('sfx_timer_warn', 'assets/audio/sfx/overwatch/sfx_timer_warn.mp3');
    this.load.audio('sfx_timer_critical', 'assets/audio/sfx/overwatch/sfx_timer_critical.mp3');
    this.load.audio('sfx_combo', 'assets/audio/sfx/overwatch/sfx_combo.mp3');
    this.load.audio('sfx_wrath', 'assets/audio/sfx/overwatch/sfx_wrath.mp3');
    this.load.audio('sfx_map_nav', 'assets/audio/sfx/overwatch/sfx_map_nav.mp3');
    this.load.audio('sfx_countdown', 'assets/audio/sfx/overwatch/sfx_countdown.mp3');
    this.load.audio('sfx_go', 'assets/audio/sfx/overwatch/sfx_go.mp3');
    this.load.audio('sfx_heart_lost', 'assets/audio/sfx/overwatch/sfx_heart_lost.mp3');
    this.load.audio('sfx_rank_reveal', 'assets/audio/sfx/overwatch/sfx_rank_reveal.mp3');

    // ─── Gameplay Sound Effects ──────────────────────────
    console.log('📦 Loading gameplay sound effects...');
    this.load.audio('sfx_projectile_fire', 'assets/audio/sfx/overwatch/sfx_projectile_fire.mp3');
    this.load.audio('sfx_projectile_charge', 'assets/audio/sfx/overwatch/sfx_projectile_charge.mp3');
    this.load.audio('sfx_projectile_hit', 'assets/audio/sfx/overwatch/sfx_projectile_hit.mp3');
    // Bubble pops use unique per-size files via bubbleConfig.json:
    // bubble_pop_large / bubble_pop_medium / bubble_pop_small / bubble_pop_tiny
    // (loaded by the bubble-sounds loop below — the old sfx_pop_* keys are retired)
    this.load.audio('sfx_powerup_collect', 'assets/audio/sfx/overwatch/sfx_powerup_collect.mp3');
    this.load.audio('sfx_shield_activate', 'assets/audio/sfx/overwatch/sfx_shield_activate.mp3');
    this.load.audio('sfx_shield_hit', 'assets/audio/sfx/overwatch/sfx_shield_hit.mp3');
    this.load.audio('sfx_shield_break', 'assets/audio/sfx/overwatch/sfx_shield_break.mp3');
    this.load.audio('sfx_timefreeze', 'assets/audio/sfx/overwatch/sfx_timefreeze.mp3');
    this.load.audio('sfx_rapidfire', 'assets/audio/sfx/overwatch/sfx_rapidfire.mp3');
    this.load.audio('sfx_player_hit', 'assets/audio/sfx/overwatch/sfx_player_hit.mp3');
    this.load.audio('sfx_player_dash', 'assets/audio/sfx/overwatch/sfx_player_dash.mp3');
    this.load.audio('sfx_player_death', 'assets/audio/sfx/overwatch/sfx_player_death.mp3');
    this.load.audio('sfx_boss_roar', 'assets/audio/sfx/overwatch/sfx_boss_roar.mp3');
    this.load.audio('sfx_boss_attack', 'assets/audio/sfx/overwatch/sfx_boss_attack.mp3');
    this.load.audio('sfx_boss_hit', 'assets/audio/sfx/overwatch/sfx_boss_hit.mp3');
    this.load.audio('sfx_boss_phase', 'assets/audio/sfx/overwatch/sfx_boss_phase.mp3');
    this.load.audio('sfx_boss_death', 'assets/audio/sfx/overwatch/sfx_boss_death.mp3');
    this.load.audio('sfx_splash', 'assets/audio/sfx/overwatch/sfx_splash.mp3');
    // Wave ambient — long loop, kept from original (not part of Overwatch overhaul)
    this.load.audio('sfx_wave_ambient', 'assets/audio/sfx/gameplay/sfx_wave_ambient.mp3');

    // ─── OCEANIC OVERWATCH VOICE STINGERS (Clyde voice) ─────────────────
    console.log('🎤 Loading Oceanic Overwatch voice stingers...');
    const overwatchVoice = [
      'voice_excellent', 'voice_victory_mine', 'voice_the_wave',
      'voice_impressive', 'voice_crushing',
      'voice_unleash_tide', 'voice_behold_wrath',
      'voice_power', 'voice_by_trident',
      'voice_sea_is_mine', 'voice_tide_turns', 'voice_feel_depths',
    ];
    for (const k of overwatchVoice) this.load.audio(k, `assets/audio/sfx/overwatch/${k}.mp3`);

    // ─── POLISH PASS AUDIO: Voice grunts, combo stingers, ambient, transitions ───
    console.log('🎤 Loading Poseidon voice grunts + polish audio...');
    this.load.audio('voice_shout_1', 'assets/audio/sfx/voice/voice_shout_1.mp3');
    this.load.audio('voice_shout_2', 'assets/audio/sfx/voice/voice_shout_2.mp3');
    this.load.audio('voice_ow', 'assets/audio/sfx/voice/voice_ow.mp3');
    this.load.audio('voice_laugh', 'assets/audio/sfx/voice/voice_laugh.mp3');
    this.load.audio('voice_victory', 'assets/audio/sfx/voice/voice_victory.mp3');
    // ElevenLabs Clyde TTS — fires on boss victory in place of the old grunt.
    this.load.audio('voice_ocean_saved', 'assets/audio/sfx/voice/voice_ocean_saved.mp3');
    this.load.audio('voice_angry', 'assets/audio/sfx/voice/voice_angry.mp3');
    this.load.audio('voice_charge', 'assets/audio/sfx/voice/voice_charge.mp3');

    this.load.audio('stinger_combo_3', 'assets/audio/sfx/stingers/stinger_combo_3.mp3');
    this.load.audio('stinger_combo_5', 'assets/audio/sfx/stingers/stinger_combo_5.mp3');
    this.load.audio('stinger_combo_10', 'assets/audio/sfx/stingers/stinger_combo_10.mp3');
    this.load.audio('stinger_combo_20', 'assets/audio/sfx/stingers/stinger_combo_20.mp3');
    this.load.audio('stinger_perfect', 'assets/audio/sfx/stingers/stinger_perfect.mp3');

    this.load.audio('amb_underwater_loop', 'assets/audio/sfx/ambient/amb_underwater_loop.mp3');
    this.load.audio('amb_tension_loop', 'assets/audio/sfx/ambient/amb_tension_loop.mp3');
    this.load.audio('sfx_whoosh_transition', 'assets/audio/sfx/ambient/sfx_whoosh_transition.mp3');
    this.load.audio('sfx_whoosh_reveal', 'assets/audio/sfx/ambient/sfx_whoosh_reveal.mp3');
    this.load.audio('sfx_iris_open', 'assets/audio/sfx/ambient/sfx_iris_open.mp3');
    this.load.audio('sfx_curtain_drop', 'assets/audio/sfx/ambient/sfx_curtain_drop.mp3');

    // AAA: Pollution pool ground hazard
    this.load.image('pollution_pool', 'assets/ui/objects/pollution_pool.png');

    // ─── AMBIENT CREATURES + ENVIRONMENT (all worlds) ───────────────────
    console.log('📦 Loading ambient creatures + environment...');

    // World 1 — underwater ambient life
    // Medusa (jellyfish) — 32-frame Grok img2vid drift cycle generated from
    // the client-supplied medusa reference. Bell pulses + tentacles trail
    // for a graceful 2s loop. Replaces the legacy 4-frame ambient_jellyfish set.
    for (let i = 1; i <= 32; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `ambient_jellyfish_${i}`,
        `assets/sprites/ambient/world1/jellyfish/medusa_drift_${n}.png`
      );
    }
    // World 1 — crab 32-frame idle/watch cycle, generated via Grok img2vid
    // (Method A in docs/SPRITE_PIPELINE.md) from the client-supplied
    // crab reference. Replaces the legacy 3-frame ambient_crab set.
    for (let i = 1; i <= 32; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `ambient_crab_${i}`,
        `assets/sprites/ambient/world1/crab/crab_idle_${n}.png`
      );
    }
    this.load.image('ambient_school_tiny', 'assets/sprites/ambient/world1/school/ambient_school_tiny.png');
    this.load.image('ambient_seahorse', 'assets/sprites/ambient/world1/seahorse/ambient_seahorse.png');

    // World 1 — sea turtle 32-frame swim cycle, generated via the FlowNode
    // Grok img2vid method (Method A in docs/SPRITE_PIPELINE.md). 2x density
    // (vs the original 16-frame extraction) for buttery-smooth interpolation.
    // Natural eye + body morphing comes from the underlying 2s video.
    for (let i = 1; i <= 32; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `ambient_turtle_${i}`,
        `assets/sprites/ambient/world1/turtle/turtle_swim_${n}.png`
      );
    }

    // World 1 — blue tang 32-frame in-place swim cycle, Grok img2vid
    // (Method A) from the client-supplied tang reference. Body undulates,
    // tail beats, fins flutter — fish stays anchored in absolute pixel
    // position across the cycle (chroma-key + union-bbox post — see
    // scripts/generate-fish-video-frames.py).
    for (let i = 1; i <= 32; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `ambient_tang_${i}`,
        `assets/sprites/ambient/world1/tang/tang_swim_${n}.png`
      );
    }

    // Educational layer assets (items that appear inside bubbles + intro art)
    this.load.image('plastic_bag', 'assets/sprites/items/plastic_bag.png');
    this.load.image('plastic_bag_2', 'assets/sprites/items/plastic_bag_2.png');
    this.load.image('bag_piece', 'assets/sprites/items/bag_piece.png');
    this.load.image('magic_dust', 'assets/sprites/items/magic_dust.png');

    // World 2 — cigarette butts + World 3 — storm clouds (popped items)
    this.load.image('cigarette_butt', 'assets/sprites/items/cigarette_butt.png');
    this.load.image('cigarette_butt_2', 'assets/sprites/items/cigarette_butt_2.png');
    this.load.image('storm_cloud', 'assets/sprites/items/storm_cloud.png');
    this.load.image('storm_cloud_2', 'assets/sprites/items/storm_cloud_2.png');

    // Educational intro card art (9:16 hero illustrations rendered inside
    // EducationIntroModal / NarrativeCardModal). Same Gemini pipeline as
    // the original world1_intro.png — see scripts/generate-w2-w3-card-images.py.
    this.load.image('world1_intro', 'assets/ui/education/world1_intro.png');
    this.load.image('world2_intro', 'assets/ui/education/world2_intro.png');
    this.load.image('world2_tip',   'assets/ui/education/world2_tip.png');
    this.load.image('world3_intro', 'assets/ui/education/world3_intro.png');
    this.load.image('world3_tip',   'assets/ui/education/world3_tip.png');
    this.load.image('boss_intro',   'assets/ui/education/boss_intro.png');

    // Animated plastic bag — 12-frame drift loop produced via
    // Gemini hero → Grok Imagine img2vid → ffmpeg extract → fal.ai PixelCut.
    // Same pipeline as the turtle swim cycle, applied to the bag to give
    // it real 3D tumble motion instead of a static CSS rotation.
    for (let i = 1; i <= 12; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `bag_drift_${i}`,
        `assets/sprites/items/bag_drift/bag_drift_${n}.png`
      );
    }
    // Animated cigarette butt drift — same pipeline (Gemini → Grok i2v
    // → ffmpeg → fal.ai). World 2 popped item.
    for (let i = 1; i <= 12; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `butt_drift_${i}`,
        `assets/sprites/items/butt_drift/butt_drift_${n}.png`
      );
    }
    // Animated storm cloud drift — same pipeline. World 3 popped item.
    for (let i = 1; i <= 12; i++) {
      const n = i.toString().padStart(2, '0');
      this.load.image(
        `storm_drift_${i}`,
        `assets/sprites/items/storm_drift/storm_drift_${n}.png`
      );
    }

    // World 2 — beach ambient life
    this.load.image('ambient_seagull_1', 'assets/sprites/ambient/world2/seagull/ambient_seagull_1.png');
    this.load.image('ambient_seagull_2', 'assets/sprites/ambient/world2/seagull/ambient_seagull_2.png');
    this.load.image('ambient_seagull_3', 'assets/sprites/ambient/world2/seagull/ambient_seagull_3.png');
    this.load.image('ambient_seagull_4', 'assets/sprites/ambient/world2/seagull/ambient_seagull_4.png');
    this.load.image('ambient_butterfly', 'assets/sprites/ambient/world2/butterfly/ambient_butterfly.png');
    this.load.image('ambient_butterfly_f2', 'assets/sprites/ambient/world2/butterfly/ambient_butterfly_f2.png');
    this.load.image('ambient_butterfly_f3', 'assets/sprites/ambient/world2/butterfly/ambient_butterfly_f3.png');
    this.load.image('ambient_sailboat', 'assets/sprites/ambient/world2/sailboat/ambient_sailboat.png');

    // World 3 — ship/open sea ambient life
    this.load.image('ambient_flyingfish', 'assets/sprites/ambient/world3/flyingfish/ambient_flyingfish.png');
    this.load.image('ambient_flyingfish_f2', 'assets/sprites/ambient/world3/flyingfish/ambient_flyingfish_f2.png');
    this.load.image('ambient_whalespout', 'assets/sprites/ambient/world3/whale/ambient_whalespout.png');
    this.load.image('ambient_storm_cloud', 'assets/sprites/ambient/world3/storm/ambient_storm_cloud.png');

    // Boss — pollution hydra ambient
    this.load.image('boss_deadfish_1', 'assets/sprites/ambient/boss/deadfish/boss_deadfish_1.png');
    this.load.image('boss_deadfish_2', 'assets/sprites/ambient/boss/deadfish/boss_deadfish_2.png');
    this.load.image('boss_debris_bottle', 'assets/sprites/ambient/boss/debris/boss_debris_bottle.png');
    this.load.image('boss_debris_bag', 'assets/sprites/ambient/boss/debris/boss_debris_bag.png');
    this.load.image('boss_debris_can', 'assets/sprites/ambient/boss/debris/boss_debris_can.png');
    this.load.image('boss_debris_rings', 'assets/sprites/ambient/boss/debris/boss_debris_rings.png');
    this.load.image('boss_dead_coral', 'assets/sprites/ambient/boss/coral/boss_dead_coral.png');
    this.load.image('boss_oil_slick', 'assets/sprites/ambient/boss/oil/boss_oil_slick.png');
    this.load.image('boss_toxic_bubble', 'assets/sprites/ambient/boss/toxic/boss_toxic_bubble.png');
    this.load.image('boss_soot_particle', 'assets/sprites/ambient/boss/soot/boss_soot_particle.png');

    // Interactive random event sprites
    this.load.image('event_treasure_chest', 'assets/sprites/ambient/events/treasure/event_treasure_chest.png');
    this.load.image('event_message_bottle', 'assets/sprites/ambient/events/bottle/event_message_bottle.png');
    this.load.image('event_shooting_star', 'assets/sprites/ambient/events/star/event_shooting_star.png');

    // Parallax background layers — deferred until proper panoramic
    // generation strategy is worked out. The GameScene/BossScene
    // installParallaxLayers() helpers check exists() and no-op cleanly
    // when no layers are loaded.

    // Load bubble sprites from configuration
    console.log('📦 Loading bubble sprites...');
    const bubbleTypes = getAllBubbleTypes();
    const loadedSprites = new Set<string>();
    
    for (const bubbleType of bubbleTypes) {
      const config = getBubbleConfig(bubbleType);
      // Only load each unique sprite once
      if (!loadedSprites.has(config.sprite)) {
        this.load.image(config.sprite, config.assetPath);
        loadedSprites.add(config.sprite);
        console.log(`  - Loading ${config.name} sprite: ${config.sprite}`);
      }
    }

    // Load bubble pop sounds from configuration
    console.log('🔊 Loading bubble sounds...');
    const bubbleSounds = getAllBubbleSounds();
    for (const sound of bubbleSounds) {
      this.load.audio(sound.key, sound.path);
      console.log(`  - Loading sound: ${sound.key}`);
    }

    // Load music from configuration
    console.log('🎵 Loading music...');
    const musicConfigs = AudioManager.getAllMusicConfigs();
    for (const music of musicConfigs) {
      this.load.audio(music.key, music.path);
      console.log(`  - Loading music: ${music.key}`);
    }

    // Load background images (world backgrounds + legacy)
    console.log('🖼️ Loading background images...');
    this.load.image('world1_bg.jpg', 'assets/backgrounds/images/world1_bg.jpg');
    console.log('  - Loading world1_bg.jpg (Coral Reef)');
    this.load.image('world2_bg.jpg', 'assets/backgrounds/images/world2_bg.jpg');
    console.log('  - Loading world2_bg.jpg (Aegean Shores)');
    this.load.image('world3_bg.jpg', 'assets/backgrounds/images/world3_bg.jpg');
    console.log('  - Loading world3_bg.jpg (Open Sea)');
    // Legacy backgrounds (kept for compatibility)

    // Shield aura sprite
    this.load.image('shield_aura', 'assets/ui/powerups/shield_aura.png');

    // Boss frames (41 frames for idle animation)
    console.log('👹 Loading boss frames...');
    for (let i = 1; i <= 41; i++) {
      const key = `boss_frame_${i}`;
      const path = `assets/sprites/boss/boss_${i.toString().padStart(3, '0')}.png`;
      this.load.image(key, path);
    }

    // Boss stance sprites
    this.load.image('boss_attack', 'assets/sprites/boss/boss_attack.png');
    this.load.image('boss_lunge', 'assets/sprites/boss/boss_lunge.png');
    this.load.image('boss_damaged', 'assets/sprites/boss/boss_damaged.png');
    this.load.image('boss_death', 'assets/sprites/boss/boss_death.png');
    // Boss pain animation frames (hit reaction)
    this.load.image('boss_pain_1', 'assets/sprites/boss/boss_pain_1.png');
    this.load.image('boss_pain_2', 'assets/sprites/boss/boss_pain_2.png');
    this.load.image('boss_pain_3', 'assets/sprites/boss/boss_pain_3.png');
    this.load.image('boss_pain_4', 'assets/sprites/boss/boss_pain_4.png');
    // Boss death dissolve animation frames (AAA death sequence)
    this.load.image('boss_death_anim_1', 'assets/sprites/boss/boss_death_anim_1.png');
    this.load.image('boss_death_anim_2', 'assets/sprites/boss/boss_death_anim_2.png');
    this.load.image('boss_death_anim_3', 'assets/sprites/boss/boss_death_anim_3.png');
    this.load.image('boss_death_anim_4', 'assets/sprites/boss/boss_death_anim_4.png');
    // Boss sludge wave attack animation frames
    this.load.image('boss_sludge_1', 'assets/sprites/boss/boss_sludge_1.png');
    this.load.image('boss_sludge_2', 'assets/sprites/boss/boss_sludge_2.png');
    this.load.image('boss_sludge_3', 'assets/sprites/boss/boss_sludge_3.png');
    this.load.image('boss_sludge_4', 'assets/sprites/boss/boss_sludge_4.png');

    // Fish animation frames (Greek Mediterranean fish — 5 species)
    const fishSpecies = ['bream', 'mullet', 'seabass', 'sardine', 'grouper'];
    for (const species of fishSpecies) {
      for (let i = 1; i <= 4; i++) {
        this.load.image(`fish_${species}_${i}`, `assets/sprites/fish/fish_${species}_${i}.png`);
      }
    }

    // Boss arena background
    this.load.image('boss_bg.jpg', 'assets/backgrounds/images/boss_bg.jpg');

    // Parallax foreground layers
    console.log('🌿 Loading parallax layers...');
    this.load.image('parallax_world1', 'assets/backgrounds/parallax/world1_fg.png');
    this.load.image('parallax_world2', 'assets/backgrounds/parallax/world2_fg.png');
    this.load.image('parallax_world3', 'assets/backgrounds/parallax/world3_fg.png');
    
    // Load projectile sprites
    console.log('🔱 Loading projectile sprites...');
    this.load.image('energyProjectile', 'assets/sprites/projectile/energyProjectile.png');
    console.log('  - Loading energyProjectile.png');
    
    // Load powerup sprites
    console.log('⚡ Loading powerup sprites...');
    const powerupAssets = getAllPowerupAssets();
    for (const asset of powerupAssets) {
      this.load.image(asset.key, asset.path);
      console.log(`  - Loading ${asset.key}: ${asset.path}`);
    }
    
    // Load obstacle sprites
    console.log('🧱 Loading obstacle sprites...');
    const obstacleAssets = getAllObstacleAssets();
    for (const asset of obstacleAssets) {
      this.load.image(asset.sprite, asset.path);
      console.log(`  - Loading ${asset.sprite}: ${asset.path}`);
    }
    
    console.log('📦 Asset loading initiated...');
  }

  async create(): Promise<void> {
    console.log('✅ Assets loaded successfully');

    // Root-cause fix for `net::ERR_CACHE_OPERATION_NOT_SUPPORTED`: pre-download
    // the menu background video into an in-memory blob BEFORE the menu starts,
    // so BackgroundManager's two crossfade <video> elements never race to
    // stream the same cold-cache file (see blobifyVideo). Bounded by an 8s cap
    // so a poor connection cannot hold the loading screen hostage after the
    // asset bar already hit 100%. On timeout we proceed to the menu and the
    // video streams (the self-heal listener covers the cold-cache race); the
    // un-awaited warmers below still finish blobbing it in memory.
    await Promise.race([
      this.blobifyVideo('bg_video_world1_video'),
      new Promise<void>((resolve) => window.setTimeout(resolve, 8000)),
    ]);
    
    // Hide loading screen
    const loadingElement = document.getElementById('loading');
    if (loadingElement) {
      loadingElement.classList.add('hidden');
    }

    // Create all player animations from loaded frames
    AnimationManager.createPlayerAnimations(this);

    // Create boss idle animation from extracted frames
    const bossFrames: Phaser.Types.Animations.AnimationFrame[] = [];
    for (let i = 1; i <= 41; i++) {
      const key = `boss_frame_${i}`;
      if (this.textures.exists(key)) {
        bossFrames.push({ key });
      }
    }
    if (bossFrames.length > 0) {
      this.anims.create({
        key: 'boss_idle',
        frames: bossFrames,
        frameRate: 12,
        repeat: -1,
      });
      console.log(`✅ Boss idle animation created (${bossFrames.length} frames)`);
    }

    console.log('✅ Bubble sprites loaded and ready');

    // Bake a subtle radial alpha falloff into every bubble sprite so the rim
    // feathers softly into the underwater backdrop instead of ending in a
    // hard PNG edge. One-time pass at boot — zero per-frame cost.
    const bubbleSpriteKeys = getAllBubbleTypes()
      .map((t) => getBubbleConfig(t).sprite);
    bakeAllBubbleEdges(this, bubbleSpriteKeys);
    console.log('✅ Bubble edge falloff baked');

    // Generate AAA particle textures programmatically
    this.generateParticleTextures();

    // Warm the remaining stage/boss videos into blobs in the background so they
    // are race-free by the time the player reaches those stages, without
    // delaying the menu. If one isn't ready in time it streams as before and
    // BackgroundManager's self-heal listener covers any transient cache abort.
    void this.blobifyVideo('bg_video_world3_video');
    void this.blobifyVideo('bg_video_world2_video');
    void this.blobifyVideo('bg_video_hydra_stage_mp4');

    // Start the menu scene
    this.scene.start(SCENE_KEYS.MENU);
  }

  /**
   * Root-cause fix for `net::ERR_CACHE_OPERATION_NOT_SUPPORTED`.
   *
   * Phaser 4's `load.video` does NOT download the file — it only resolves and
   * stores the URL, and every Video Game Object then streams that URL itself.
   * BackgroundManager creates TWO video objects per background (a crossfade
   * pair) that both play from the first frame, so on a cold cache two <video>
   * elements race to fetch AND write the same MP4 into Chrome's sparse media
   * cache. The write collides, Chrome aborts one stream a few seconds in — the
   * reported "background video disappears after 3-5s on first load, fine after
   * a refresh" on Windows Chrome.
   *
   * Fix: download the file ONCE here and rewrite the cache entry's URL to an
   * in-memory `blob:` object URL. Both video elements then read from memory —
   * no network range requests, no cache writes, no race, and a single download
   * instead of two. Existing `add.video(key)` call sites need no changes.
   *
   * Best-effort: on any failure we leave the original network URL untouched so
   * playback simply falls back to the previous (streaming) behaviour.
   */
  private async blobifyVideo(key: string): Promise<void> {
    const entry = this.cache.video.get(key) as { url?: string } | undefined;
    if (!entry || !entry.url) return;
    if (entry.url.startsWith('blob:')) return; // already blobbed
    try {
      const resp = await fetch(entry.url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      entry.url = URL.createObjectURL(blob);
      console.log(`🎞️ Cached background video '${key}' as in-memory blob (${(blob.size / 1048576).toFixed(1)}MB)`);
    } catch (err) {
      console.warn(`⚠️ Could not blob video '${key}', leaving network URL:`, err);
    }
  }

  /**
   * Generate high-quality particle sprite textures
   */
  private generateParticleTextures(): void {
    // Soft glow particle (radial gradient circle)
    if (!this.textures.exists('particle_glow')) {
      const size = 32;
      const canvas = this.textures.createCanvas('particle_glow', size, size);
      if (canvas) {
        const ctx = canvas.context;
        const gradient = ctx.createRadialGradient(size/2, size/2, 0, size/2, size/2, size/2);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.6)');
        gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.15)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
        canvas.refresh();
      }
    }

    // Star/sparkle particle (4-pointed star)
    if (!this.textures.exists('particle_star')) {
      const g = this.add.graphics();
      const s = 16;
      g.fillStyle(0xFFFFFF, 1);
      // Diamond shape
      g.fillTriangle(s, 0, s*2, s, s, s*2);
      g.fillTriangle(s, 0, 0, s, s, s*2);
      // Cross rays
      g.fillRect(s - 1, 0, 2, s * 2);
      g.fillRect(0, s - 1, s * 2, 2);
      g.generateTexture('particle_star', s * 2, s * 2);
      g.destroy();
    }

    // Bubble particle (hollow circle with highlight)
    if (!this.textures.exists('particle_bubble')) {
      const size = 24;
      const canvas = this.textures.createCanvas('particle_bubble', size, size);
      if (canvas) {
        const ctx = canvas.context;
        // Outer ring
        ctx.beginPath();
        ctx.arc(size/2, size/2, size/2 - 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 2;
        ctx.stroke();
        // Highlight dot
        ctx.beginPath();
        ctx.arc(size/2 - 3, size/2 - 3, 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fill();
        canvas.refresh();
      }
    }

    console.log('✅ AAA particle textures generated');
  }

  private createLoadingScreen(): void {
    const { width, height } = this.cameras.main;
    const cx = width / 2;
    const cy = height / 2;

    // ─── Deep-sea crystal gradient background ────────────────────────────
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x020816, 0x020816, 0x103053, 0x103053, 1);
    bg.fillRect(0, 0, width, height);
    bg.fillStyle(0x2a64d8, 0.08);
    bg.fillEllipse(width * 0.2, height * 0.12, 720, 320);
    bg.fillStyle(0x2cc7e8, 0.08);
    bg.fillEllipse(width * 0.78, height * 0.18, 620, 260);
    bg.fillStyle(0xffffff, 0.03);
    bg.fillEllipse(cx, cy - 110, 560, 220);

    // ─── Animated rising bubbles (pure graphics, no asset dependency) ─────
    const bubbles: { g: Phaser.GameObjects.Arc; vy: number; r: number }[] = [];
    for (let i = 0; i < 40; i++) {
      const r = 3 + Math.random() * 8;
      const b = this.add.circle(
        Math.random() * width,
        height + Math.random() * 400,
        r,
        0x4dd0e1,
        0.3 + Math.random() * 0.3,
      );
      b.setBlendMode(Phaser.BlendModes.ADD);
      bubbles.push({ g: b, vy: -20 - Math.random() * 40, r });
    }
    const bubbleUpdate = this.time.addEvent({
      delay: 33,
      loop: true,
      callback: () => {
        const dt = 0.033;
        for (const b of bubbles) {
          b.g.y += b.vy * dt;
          b.g.x += Math.sin(this.time.now / 500 + b.g.y / 50) * 0.4;
          if (b.g.y < -20) {
            b.g.y = height + 20;
            b.g.x = Math.random() * width;
          }
        }
      },
    });

    // ─── Light rays from above ────────────────────────────────────────────
    for (let i = 0; i < 3; i++) {
      const rx = width * 0.2 + i * width * 0.3;
      const ray = this.add.graphics();
      ray.fillStyle(0xFFFFFF, 0.04);
      ray.fillTriangle(rx - 30, -20, rx + 30, -20, rx + 60 + i * 20, height * 0.9);
      this.tweens.add({
        targets: ray,
        alpha: { from: 0.03, to: 0.08 },
        duration: 2200 + i * 250,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // ─── Center crystal core ──────────────────────────────────────────────
    const aura = this.add.graphics();
    aura.fillStyle(0x2cc7e8, 0.14);
    aura.fillCircle(cx, cy - 70, 110);
    aura.fillStyle(0xffffff, 0.08);
    aura.fillCircle(cx, cy - 70, 64);

    const coreRing = this.add.graphics();
    coreRing.lineStyle(3, 0x9df4ff, 0.55);
    coreRing.strokeCircle(cx, cy - 70, 82);
    coreRing.lineStyle(1, 0xffffff, 0.22);
    coreRing.strokeCircle(cx, cy - 70, 96);

    const corePulse = this.add.circle(cx, cy - 70, 14, 0xf3fbff, 0.95).setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: corePulse,
      scale: { from: 0.92, to: 1.18 },
      alpha: { from: 0.75, to: 1 },
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: coreRing,
      alpha: { from: 0.7, to: 1 },
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Game name intentionally omitted from the loading screen (per direction).
    // The crystal core + the progress cluster carry the screen on their own.
    const status = this.add.text(cx, cy + 30, 'Charging the trident', {
      fontSize: '22px',
      fontFamily: 'Cormorant Garamond',
      color: '#BFD4E6',
      fontStyle: 'italic',
      stroke: '#06111F',
      strokeThickness: 1,
    }).setOrigin(0.5);

    // ─── Progress cluster layout ─────────────────────────────────────────
    const barW = 460, barH = 14, barY = cy + 150;

    // Big 0–100% readout sitting directly on top of the bar.
    const progressPercent = this.add.text(cx, barY - 58, '0%', {
      fontSize: '66px',
      fontFamily: 'CF Asty Std',
      color: '#F3FBFF',
      fontStyle: 'bold',
      letterSpacing: 1,
      stroke: '#06111F',
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 0, color: '#2cc7e8', blur: 26, fill: true },
    }).setOrigin(0.5);
    // Gentle breathing pulse so the number feels alive while it climbs.
    this.tweens.add({
      targets: progressPercent,
      scale: { from: 0.985, to: 1.02 },
      duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Bar track.
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x06111f, 0.9);
    progressBox.fillRoundedRect(cx - barW / 2, barY, barW, barH, 999);
    progressBox.lineStyle(2, 0x2d5f94, 0.6);
    progressBox.strokeRoundedRect(cx - barW / 2, barY, barW, barH, 999);
    progressBox.lineStyle(1, 0xffffff, 0.16);
    progressBox.strokeRoundedRect(cx - barW / 2 + 3, barY + 3, barW - 6, barH - 6, 999);

    const progressBar = this.add.graphics();

    // ─── "NOW LOADING · <category>" — names what is streaming in ──────────
    const eyebrow = this.add.text(cx, barY + 40, 'NOW LOADING', {
      fontSize: '13px', fontFamily: 'CF Asty Std', color: '#5E89B0',
      letterSpacing: 4, stroke: '#06111F', strokeThickness: 1,
    }).setOrigin(0.5);
    const loadingLabel = this.add.text(cx, barY + 66, 'PREPARING THE TIDES', {
      fontSize: '24px', fontFamily: 'CF Asty Std', color: '#9DF4FF',
      fontStyle: 'bold', letterSpacing: 3, stroke: '#06111F', strokeThickness: 2,
    }).setOrigin(0.5);
    const assetCount = this.add.text(cx, barY + 98, '', {
      fontSize: '14px', fontFamily: 'Cormorant Garamond', color: '#6F8DA6', letterSpacing: 1,
    }).setOrigin(0.5);

    // HELMEPA credit
    const credit = this.add.text(cx, height - 40, 'A HELMEPA production', {
      fontSize: '16px', fontFamily: 'Cormorant Garamond', color: '#6F8DA6',
    }).setOrigin(0.5);

    // Map an asset's loader key/type onto a friendly, player-facing group.
    const categoryFor = (key: string, type: string): string => {
      const k = (key || '').toLowerCase();
      if (type === 'video' || k.includes('video')) return 'CINEMATICS';
      if (type === 'audio' || k.startsWith('sfx') || k.startsWith('music') || k.includes('audio')) return 'SOUND & MUSIC';
      if (type === 'font' || type === 'webfont' || k.includes('asty') || k.includes('skranji') || k.includes('cormorant') || k.includes('garamond')) return 'FONTS';
      if (k.includes('poseidon')) return 'POSEIDON';
      if (k.includes('boss') || k.includes('hydra')) return 'THE HYDRA';
      if (k.includes('bubble')) return 'BUBBLES';
      if (k.includes('sky') || k.includes('parallax') || k.includes('background') || k.includes('arena')) return 'BACKGROUNDS';
      if (k.includes('powerup') || k.includes('emboss')) return 'POWER-UPS';
      if (k.includes('fish') || k.includes('crab') || k.includes('turtle') || k.includes('jelly') || k.includes('ambient')) return 'SEA LIFE';
      if (k.includes('medal') || k.includes('leaderboard') || k.includes('rank') || k.includes('star')) return 'LEADERBOARD';
      if (k.includes('btn') || k.includes('button') || k.includes('frame') || k.includes('hud') || k.includes('menu') || k.includes('icon') || k.includes('logo') || k.includes('title') || k.includes('key_tile') || k.includes('input')) return 'INTERFACE';
      return 'GAME ASSETS';
    };

    // ─── Progress events ─────────────────────────────────────────────────
    // fileprogress fires per file as it streams; update the category label
    // only when the group actually changes so it reads as a clean progression
    // (POSEIDON → CINEMATICS → SOUND & MUSIC → …) rather than thrashing.
    let lastCategory = '';
    let lastLabelAt = 0;
    this.load.on('fileprogress', (file: { key?: string; type?: string }) => {
      const cat = categoryFor(file?.key || '', file?.type || '');
      if (cat === lastCategory) return;
      // Parallel downloads interleave categories, so the raw "current file"
      // category flips ~20×/sec — unreadable. Throttle to a calm cadence so the
      // label steps through groups at a pace the eye can actually follow.
      const now = Date.now();
      if (now - lastLabelAt < 450) return;
      lastLabelAt = now;
      lastCategory = cat;
      console.log(`📂 Loading ${cat}…`);
      loadingLabel.setText(cat);
      loadingLabel.setScale(1.08);
      this.tweens.add({ targets: loadingLabel, scale: 1, duration: 220, ease: 'Quad.easeOut' });
    });

    this.load.on('progress', (value: number) => {
      const fillW = barW * value;
      progressBar.clear();
      progressBar.fillStyle(0x9df4ff, 0.22);
      progressBar.fillRoundedRect(cx - barW / 2, barY, fillW, barH, 999);
      progressBar.fillGradientStyle(0xf3fbff, 0xf3fbff, 0x2cc7e8, 0x2a64d8, 1, 1, 1, 1);
      progressBar.fillRoundedRect(cx - barW / 2 + 2, barY + 2, Math.max(0, fillW - 4), barH - 4, 999);
      // Bright leading-edge node so the fill reads as a charging current.
      if (fillW > 8) {
        progressBar.fillStyle(0xffffff, 0.95);
        progressBar.fillCircle(cx - barW / 2 + Math.min(fillW, barW) - barH / 2, barY + barH / 2, barH / 2 - 1);
      }
      progressPercent.setText(`${Math.round(value * 100)}%`);
      const total = Math.round((this.load as { totalToLoad?: number }).totalToLoad || 0);
      const done = Math.round((this.load as { totalComplete?: number }).totalComplete || 0);
      if (total > 0) assetCount.setText(`${Math.min(done, total)} / ${total} assets`);
    });

    this.load.on('complete', () => {
      bubbleUpdate.destroy();
      loadingLabel.setText('READY');
      progressPercent.setText('100%');
      const elements = [
        bg, aura, coreRing, corePulse, status,
        progressPercent, progressBox, progressBar, eyebrow, loadingLabel, assetCount,
        credit, ...bubbles.map(b => b.g),
      ];
      this.tweens.add({
        targets: elements,
        alpha: 0,
        duration: 500,
        delay: 200,
        onComplete: () => elements.forEach((el: Phaser.GameObjects.GameObject) => el.destroy()),
      });
    });
  }

  // NOTE: Animation creation will be added when sprite sheets are implemented
  // Currently using static images (idle.png, walk.png, shoot.png)
}
